import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "src/components/Button/BackButton";
import { setPageNameHeader } from "src/redux/actions/header";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService, PurchaseOrderInput, PurchaseOrderRecord } from "src/services/Firebase/data";
import { Content, ContentFrame } from "src/styles";
import styled from "styled-components";
import PurchaseOrderForm from "./PurchaseOrderForm";

const PurchaseOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: purchaseOrderId } = useParams<{ id?: string }>();
  const { id: clientUserId, isManager } = useSelector(getUserData);

  const isCreateRoute = useMemo(() => !purchaseOrderId, [purchaseOrderId]);
  const [isLoading, setIsLoading] = useState(!isCreateRoute);
  const [editingRow, setEditingRow] = useState<PurchaseOrderRecord | null>(null);
  const [loadError, setLoadError] = useState("");

  const prefillForManualCreate: Partial<PurchaseOrderInput> = {
    source: "purchasing_tab",
    documentType: "purchase_order",
    status: "draft",
  };

  useEffect(() => {
    dispatch(setPageNameHeader("Purchasing"));

    return () => {
      dispatch(setPageNameHeader(""));
    };
  }, []);

  useEffect(() => {
    if (isCreateRoute) {
      setIsLoading(false);
      setEditingRow(null);
      setLoadError("");
      return;
    }

    const loadPurchaseOrder = async () => {
      setIsLoading(true);
      setLoadError("");

      try {
        const { data } = await FirebaseDataService.getPurchaseOrders(clientUserId, !!isManager);

        const row = (Array.isArray(data) ? data : []).find((item) => String(item?.id) === String(purchaseOrderId));

        console.log("Found purchase order row:", row);

        if (!row) {
          setEditingRow(null);
          setLoadError("Purchase document not found.");
          return;
        }

        setEditingRow(row);
      } catch {
        setEditingRow(null);
        setLoadError("Unable to load purchase document.");
      } finally {
        setIsLoading(false);
      }
    };

    void loadPurchaseOrder();
  }, [clientUserId, isManager, isCreateRoute, purchaseOrderId]);

  const onExit = () => {
    navigate("/purchasing");
  };

  return (
    <Content>
      <ContentFrame>
        <BackButton backHandler={onExit} />

        {isLoading ? (
          <LoadingState>
            <Spinner animation="border" size="sm" />
          </LoadingState>
        ) : loadError ? (
          <ErrorState>{loadError}</ErrorState>
        ) : (
          <PurchaseOrderForm
            initialData={
              editingRow
                ? {
                    ...editingRow,
                    poDate: editingRow.poDate?.slice(0, 10),
                    requestedDeliveryDate: editingRow.requestedDeliveryDate?.slice(0, 10),
                  }
                : prefillForManualCreate
            }
            purchaseOrderId={editingRow?.id}
            submitText={editingRow ? "Save Changes" : "Generate Purchase Order"}
            enableClientLocationSelection
            onCancel={onExit}
            onSaved={onExit}
          />
        )}
      </ContentFrame>
    </Content>
  );
};

const LoadingState = styled.div`
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorState = styled.div`
  border: 1px solid #d7dbe0;
  background: #ffffff;
  padding: 26px;
  font-size: 13px;
  color: #d33636;
`;

export default PurchaseOrder;
